<template>
<div>
    <b-table
        id="chapters-table"
        ref="table"
        class="table-panels"
        :busy.sync="isBusy"
        :items="tableItems"
        :fields="fields"
        sort-by="ended_at"
        :sort-desc="true"
        fixed
        borderless
        sort-icon-right
        :per-page="pagination.perPage"
        :current-page="pagination.currentPage"
    >
        <template #cell(superview)="data">
            <div class="creator-wrapper my-auto d-flex" v-if="data.item.superview">
                <b-img-lazy
                    :src="data.item.superview.channel.logo"
                    class="avatar my-auto"
                />

                <div
                    class="name-rank-wrapper d-flex flex-column my-auto ml-3"
                >
                    <span class="h6 m-0">
                        {{ data.item.superview.channel.display_name }}

                        <verified-badge
                            v-if="data.item.superview.channel.partner"
                            :platform="'twitch'"
                        />
                    </span>

                    <small
                        :style="{
                            color: getColors.ranks[data.item.superview.rank.name]
                        }"
                    >
                        {{ data.item.superview.rank.name | capitalize }}
                    </small>
                </div>
            </div>
        </template>
    </b-table>

    <div class="d-flex">
        <b-pagination
            class="mx-auto"
            :total-rows="pagination.totalRows"
            :per-page="pagination.perPage"
            v-model="pagination.currentPage"
         />
    </div>
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import numeral from 'numeral'
import VerifiedBadge from '@/components/Client/VerifiedBadge'

export default {
    components: {
        VerifiedBadge,
    },

    props: {
        gameId: {
            type: String,
        },

        dateRangeStart: {
            type: String,
        },

        dateRangeEnd: {
            type: String,
        },

        filters: {
            type: Object,
        },

        items: {
            type: Array,
        },
    },

    watch: {
        dateRangeStart(newValue) {
            this.$root.$emit('bv::refresh::table', 'chapters-table')
        },

        filters: {
            deep: true,

            // We have to move our method to a handler field
            handler(newValue) {
                const debouncedRefresh = _.debounce(this.$refs.table.refresh, 1000)()
            }
        },
    },

    data() {
        return {
            isBusy: false,

            pagination: {
                perPage: 10,
                currentPage: 1,
                totalRows: 0,
            },

            fields: [{
                key: 'chapter.stream_id',
                label: 'Stream',
                sortable: true,
                formatter: (value, key, item) => {
                    if (value) {
                        return value
                    } else {
                        return '---'
                    }
                },
            }, {
                key: 'chapter.game_name',
                label: 'Game',
                sortable: true,
                formatter: (value, key, item) => {
                    if (value) {
                        return value
                    } else {
                        return '---'
                    }
                },
            }, {
                key: 'chapter.statistics.avg_viewers',
                label: 'ACV',
                sortable: true,
                formatter: (value, key, item) => {
                    if (value) {
                        return numeral(value).format('0,0')
                    } else {
                        return '---'
                    }
                },
            }, {
                key: 'chapter.statistics.peak_viewers.value',
                label: 'Peak Viewers',
                sortable: true,
                formatter: (value, key, item) => {
                    if (value) {
                        return numeral(value).format('0,0')
                    } else {
                        return '---'
                    }
                },
            }, {
                key: 'chapter.statistics.avg_chatters',
                label: 'Engagement',
                sortable: true,
                formatter: (value, key, item) => {
                    if (value) {
                        const engagement = item.chapter.statistics.avg_chatters / item.chapter.statistics.avg_viewers
                        return numeral(engagement).format('0%')
                    } else {
                        return '---'
                    }
                },
            }, {
                key: 'chapter.statistics.views_gain',
                label: 'Impressions',
                sortable: true,
                formatter: (value, key, item) => {
                    if (value) {
                        return numeral(value).format('0,0')
                    } else {
                        return '---'
                    }
                },
            }, {
                key: 'chapter.duration_seconds',
                label: 'Duration',
                sortable: true,
                sortDirection: 'desc',
                formatter: (value, key, item) => {
                    const hours = Math.floor(value / (60 * 60))
                    const minutes = Math.round((value % (60 * 60)) / 60)

                    let result = []
                    if (hours > 0) result.push(`${hours}h`)
                    if (minutes > 0) result.push(`${minutes}m`)
                    return result.join(' ')
                }
            }, {
                key: 'chapter.started_at',
                label: 'Started',
                sortable: true,
                sortDirection: 'desc',
                formatter: (value, key, item) => {
                    return moment(value).format('MM/DD/YY hh:mm A')
                },
            }, {
                key: 'chapter.ended_at',
                label: 'Ended',
                sortable: true,
                sortDirection: 'desc',
                formatter: (value, key, item) => {
                    return moment(value).format('MM/DD/YY hh:mm A')
                },
            }],
        }
    },

    computed: {
        ...mapGetters([
            'getColors'
        ]),

        tableItems() {
            if (this.items) {
                return this.items
            }

            return this.provider
        }
    },

    methods: {
        provider(ctx) {
            this.isBusy = true

            const params = {
                offset: (ctx.currentPage - 1) * ctx.perPage,
                limit: ctx.perPage,
                order_field: ctx.sortBy,
                order_direction: ctx.sortDesc ? 'desc' : 'asc',
                date_range_start: moment.utc(this.dateRangeStart).format(),
                date_range_end: moment.utc(this.dateRangeEnd).format(),
            }

            // Attach filters to params
            _.assign(params, this.filters)

            return this.axios.get(`/games/${this.gameId}/twitch/chapters`, {
                params,
            }).then(({ data }) => {
                this.isBusy = false
                this.pagination.totalRows = data.payload.count
                return data.payload.chapters
            }).catch((error) => {
                console.error(`Error in table provider`, error)
                this.isBusy = false
                return []
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.table-panels {
    border-collapse:separate;
    border-spacing: 0 0.5rem;
    transition: opacity 0.25s;

    &[aria-busy="true"] {
        opacity: 0.5;
    }

    thead {
        th {
            font-weight: 400;
            line-height: 1.3;
            color: #f6f6f8;
            font-size: 1rem;
        }
    }

    tbody {
        tr {
            background: rgba(white, 0.05);
            border-radius: 5px;
            margin-bottom: 5px;

            &:hover {
                background-color: rgba(white, 0.075);
            }

            td {
                vertical-align: middle;
            }

            td:first-child {
                border-top-left-radius: 12px;
                border-bottom-left-radius: 12px;
            }

            td:last-child {
                border-top-right-radius: 12px;
                border-bottom-right-radius: 12px;
            }
        }
    }
}
</style>
